import {
	object as zodObject,
	enum as zodEnum,
	array as zodArray,
	type infer as zodInfer,
	boolean as zodBoolean,
	number as zodNumber,
	optional as zodOptional,
	string as zodString,
} from 'zod/mini';

import { PointOfCareTerminologyVariant } from './terminology/DiagnosesTerminologyModel';

import type { FeatureFlagName } from './FeatureFlagNames';

const HEADER_WIDGET_PLACEMENT_SCHEMA = zodEnum(['primary', 'secondary']);

const WIDGET_KEYS_SCHEMA = zodEnum([
	'Mrn',
	'LastVisit',
	'PatientInfo',
	'DateAndGender',
	'UpcomingVisit',
	'LastNavinaRun',
	'VisitDate',
	'ScrubDate',
	'Ascvd',
	'PatientStatus',
	'Allergies',
	'VisitType',
	'LastWellnessVisit',
	'ProviderName',
	'GroupPracticeName',
]);

export type WidgetKey = zodInfer<typeof WIDGET_KEYS_SCHEMA>;

const HEADER_WIDGET_SCHEMA = zodObject({
	key: WIDGET_KEYS_SCHEMA,
	placement: HEADER_WIDGET_PLACEMENT_SCHEMA,
});

const POINT_OF_CARE_TERMINOLOGY_VARIANT_SCHEMA = zodEnum(PointOfCareTerminologyVariant);

export const DEFAULT_CO_BRANDING_LOGO_ZOOM = 1;
export const DEFAULT_CO_BRANDING_LOGO_OFFSET_IN_PX = 0;

const CO_BRANDING_LOGO_SETUP_SCHEMA = zodObject({
	whiteLogoPath: zodString(),
	colorLogoPath: zodString(),
	zoom: zodNumber(),
	horizontalOffsetInPx: zodNumber(),
	verticalOffsetInPx: zodNumber(),
});

// Only add flags that you want to validate and later consume from the FeatureFlag MobX model
export const ALL_FEATURE_FLAGS_SCHEMA = zodObject({
	configuration_CoreServices_PatientPortraitHeaderWidgets: zodArray(HEADER_WIDGET_SCHEMA),
	configuration_CoreServices_CoBrandingLogo: zodString(),
	configuration_CoreServices_CoBrandingLogoSetup: zodOptional(CO_BRANDING_LOGO_SETUP_SCHEMA),
	ShowFeedbackButton: zodBoolean(),
	configuration_DxCapture_Show_Raf: zodBoolean(),
	configuration_DxCapture_portrait_without_sid_actions: zodBoolean(),
	configuration_DxCapture_disable_actions: zodBoolean(),
	configuration_DxCapture_yearSwitcher: zodBoolean(),
	configuration_DxCapture_patientWithoutAppointment: zodBoolean(),
	configuration_DxCapture_TerminologyVariant: POINT_OF_CARE_TERMINOLOGY_VARIANT_SCHEMA,
	configuration_showAuditLog: zodBoolean(),
	flag_DxCapture_BypassAlwaysShowAsOpenHccGap: zodBoolean(),
	flag_DxCapture_DualHcc: zodBoolean(),
	flag_DxCapture_HccGapCountTag: zodBoolean(),
	configuration_quality_showTabClinicalStatus: zodBoolean(),
	configuration_quality_showTabCareTeamStatus: zodBoolean(),
	configuration_quality_show_standalone_orders: zodBoolean(),
	configuration_ambient_suggestVisitCode: zodBoolean(),
	flag_AdminMeasuresConfiguration: zodBoolean(),
	configuration_ambient_enableTabConfiguration: zodBoolean(),
	EMRSyncHCCLevel: zodBoolean(),
	IsPushNoteByDefault: zodBoolean(),
	flag_ShallPushDxNotes: zodBoolean(),
	configuration_DxCapture_canAddManualDiagnosis: zodBoolean(),
	configuration_AddHCCFromDocuments: zodBoolean(),
	configuration_DxCapture_show_reopened_label_in_raf_list: zodBoolean(),
	flag_navina_patient_based_portrait: zodBoolean(),
	flag_CoreServices_DocumentViewerExperience: zodBoolean(),
} as const satisfies Partial<Record<FeatureFlagName, unknown>>);

export type AllFeatureFlags = zodInfer<typeof ALL_FEATURE_FLAGS_SCHEMA>;

export type HeaderWidget = zodInfer<typeof HEADER_WIDGET_SCHEMA>;

export type CoBrandingLogoSetup = zodInfer<typeof CO_BRANDING_LOGO_SETUP_SCHEMA>;
